import { useLazyQuery } from '@apollo/client';
import { CardControl } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/CardControl';
import { QuickExpand } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickExpand';
import { QuickSave } from 'components/detail/edit/EditDetail/EditDetailCards/cards/shared/QuickSave';
import { GradientLine } from 'components/detail/minute/GradientLine';
import { ImageValuesPreview } from 'components/detail/minute/ImageValuesPreview';
import { ScrollHorizontal } from 'elements/functional/ScrollHorizontal';
import { TextValue as TextValue2 } from 'elements/minute/sidebar/sidebarComponents/atomic/TextValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { SidebarSubsection } from 'elements/minute/sidebar/sidebarSection/SidebarSubsection';
import { motion } from 'framer-motion';
import { GIF_SEARCH_QUERY } from 'graphql/query/gifQuery';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EHovers } from 'libs/styles/styling';
import { useContext, useEffect, useState } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';
import { useDebounce } from 'usehooks-ts';

interface Props {
    position: number;
    className?: string;
}

export const EditGIFCard = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const card = ctxDetail.updateDetailInput.cards[props.position];
    const [search, setSearch] = useState<string>('');
    const debouncedSearchTerm = useDebounce(search, 200);
    const [results, setResults] = useState<
        {
            gifUrl: string;
            previewUrl: string;
        }[]
    >([]);
    const [gifUrl, setGifUrl] = useState<string>(card.gif.gifUrl);
    const [gif] = useLazyQuery(GIF_SEARCH_QUERY);
    const isUpdatingIndex = ctxDetail.updatingIndex === props.position;

    function changeSearch(value: string): void {
        setSearch(value);
    }

    function changeGifUrl(value: string, deselect?: boolean): void {
        setGifUrl(value);
        setResults([]);
        ctxDetail.updateCardByPosition(
            {
                gif: {
                    ...card.gif,
                    gifUrl: value
                }
            },
            props.position
        );
        if (deselect) {
            ctxDetail.setUpdatingIndex(null);
        }
    }

    useEffect(() => {
        async function search() {
            if (!debouncedSearchTerm) return;

            const res = await gif({
                variables: {
                    query: debouncedSearchTerm,
                    offset: 0
                }
            });
            setResults(res.data.gifSearch);
        }
        search();
    }, [debouncedSearchTerm]);

    return (
        <motion.div {...conditionalScaleAnimation(true, 1.02, 1.02)}>
            <S_MinuteCard
                className={props.className}
                $isUpdatingIndex={isUpdatingIndex}
                onClick={() => {
                    if (!isUpdatingIndex) {
                        ctxDetail.setUpdatingIndex(props.position);
                    }
                }}
            >
                <S_SidebarSectionHeader
                    $isUpdatingIndex={isUpdatingIndex}
                    icon={{
                        iconName: 'alien-8bit'
                    }}
                    title="GIF"
                    highlighted={!!isUpdatingIndex}
                    topBorder
                >
                    {isUpdatingIndex && <QuickSave />}
                    {!isUpdatingIndex && <QuickExpand />}
                </S_SidebarSectionHeader>
                {!isUpdatingIndex && (
                    <S_Content $isUpdatingIndex={isUpdatingIndex}>
                        <S_ImageValuesPreview images={[gifUrl]} size={125} />
                    </S_Content>
                )}
                {isUpdatingIndex && (
                    <>
                        <SidebarSubsection
                            title={Localization.SEARCH}
                            icon={{ iconName: 'magnifying-glass' }}
                        >
                            <SidebarContent>
                                <S_ImageValuesPreview2 images={[gifUrl]} size={50} />
                                <S_MinuteSearch>
                                    <S_TextValue2
                                        value={search}
                                        onChange={changeSearch}
                                        textAlign="left"
                                        placeholder={Localization.SEARCH_GIFS}
                                    />
                                    {results.length > 0 && (
                                        <S_MinuteList light={true}>
                                            {results.map((result) => {
                                                return (
                                                    <S_GifPreview
                                                        size={70}
                                                        onClick={() => {
                                                            changeGifUrl(result.gifUrl, true);
                                                        }}
                                                        key={result.previewUrl}
                                                        images={[result.previewUrl]}
                                                    />
                                                );
                                            })}
                                        </S_MinuteList>
                                    )}
                                </S_MinuteSearch>
                            </SidebarContent>
                        </SidebarSubsection>

                        <CardControl position={props.position} />
                        <S_GradientLine />
                    </>
                )}
            </S_MinuteCard>
        </motion.div>
    );
};

const S_MinuteCard = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
    border-right: 1px solid #d3d3d3;
    border-left: 1px solid #d3d3d3;
    border-bottom: 1px solid #d3d3d3;
    ${(props) => {
        if (props.$isUpdatingIndex) {
            return css`
                border-right: 1px solid #8f3a19;
                border-left: 1px solid #b19052;
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_MinuteSearch} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
    ${(props) => {
        if (!props.$isUpdatingIndex) {
            return css`
                &:hover {
                    ${S_SidebarSectionHeader} {
                        box-shadow: ${EHovers.DARK1};
                    }
                    ${S_Content} {
                        box-shadow: ${EHovers.LIGHT4};
                        cursor: pointer;
                    }
                }
            `;
        }
    }}
`;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{ $isUpdatingIndex: boolean }>`
    cursor: ${(props) => (props.$isUpdatingIndex ? 'unset' : 'pointer')};
`;

const S_ImageValuesPreview = styled(ImageValuesPreview)`
    border: unset;
`;

const S_GradientLine = styled(GradientLine)`
    margin: 15px 0 0 0;
`;

const S_TextValue2 = styled(TextValue2)``;

const S_MinuteSearch = styled.div`
    padding: 10px;
`;

const S_Content = styled.div<{ $isUpdatingIndex: boolean }>`
    display: flex;
    flex-direction: column;
`;

const S_MinuteList = styled(ScrollHorizontal)`
    display: flex;
`;

const S_GifPreview = styled(ImageValuesPreview)`
    border: unset;
    padding: 10px 0 5px 0;
    cursor: pointer;
`;

const S_ImageValuesPreview2 = styled(ImageValuesPreview)`
    border: unset;
    padding: 0 0 0 10px;
`;
